import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Input, Box, Tooltip, HStack, IconButton, useToast } from '@chakra-ui/react';
import { CopyIcon, EditIcon } from '@chakra-ui/icons';
import TextareaAutosize from 'react-textarea-autosize';
import ReactMarkdown from 'react-markdown';

// Markdown component overrides — styled for field display, not chat bubbles
const fieldMarkdownComponents = {
  p: ({ children }) => <p style={{ margin: '0 0 6px 0', lineHeight: '1.6' }}>{children}</p>,
  strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
  em: ({ children }) => <em>{children}</em>,
  ol: ({ children }) => <ol style={{ paddingLeft: '20px', margin: '4px 0', listStyleType: 'decimal' }}>{children}</ol>,
  ul: ({ children }) => <ul style={{ paddingLeft: '20px', margin: '4px 0', listStyleType: 'disc' }}>{children}</ul>,
  li: ({ children }) => <li style={{ marginBottom: '4px', lineHeight: '1.5', display: 'list-item' }}>{children}</li>,
  h1: ({ children }) => <p style={{ fontWeight: 700, fontSize: '1.1em', margin: '6px 0 4px 0' }}>{children}</p>,
  h2: ({ children }) => <p style={{ fontWeight: 700, margin: '6px 0 4px 0' }}>{children}</p>,
  h3: ({ children }) => <p style={{ fontWeight: 600, margin: '4px 0' }}>{children}</p>,
};

const preprocessContent = (content) => {
  if (!content || typeof content !== 'string') return content || '';
  
  let processed = content;
  processed = processed.replace(/([^-*\n\s])[-*]\s+/g, '$1\n- ');
  processed = processed.replace(/([a-zA-Z])\.[-*]\s+/g, '$1\n- ');
  
  return processed;
};

const EditableField = forwardRef(({ value, onChange, placeholder = '', isMultiLine = false, alt = 'Edit', isReadOnly = false, onRefineWithPabs, fieldLabel = '', actionsPlacement = 'bottom', onEditingChange }, ref) => {
  const [isEditing, setIsEditingState] = useState(false);
  const setIsEditing = (next) => {
    setIsEditingState(next);
    onEditingChange?.(next);
  };
  const [editValue, setEditValue] = useState(() => {
    let rawValue;
    if (typeof value === 'string') {
      rawValue = value || '';
    } else if (Array.isArray(value)) {
      rawValue = value.join('\n');
    } else if (value && typeof value === 'object') {
      rawValue = JSON.stringify(value, null, 2);
    } else if (value === null || value === undefined) {
      rawValue = '';
    } else {
      rawValue = String(value);
    }
    return preprocessContent(rawValue);
  });
  const textareaRef = useRef(null);
  const toast = useToast();

  const handleBlur = () => {
    setIsEditing(false);
    onChange(editValue);
  };

  const handleInputChange = (e) => {
    setEditValue(e.target.value);
  };

  const handleClick = () => {
    if (isReadOnly) return;
    const preprocessed = preprocessContent(editValue);
    if (preprocessed !== editValue) {
      setEditValue(preprocessed);
    }
    setIsEditing(true);
  };

  const handleCopy = (e) => {
    e?.stopPropagation();
    const textToCopy = typeof value === 'string' ? value : (Array.isArray(value) ? value.join('\n') : String(value || ''));
    navigator.clipboard.writeText(textToCopy);
    toast({
      title: "Copied",
      status: "success",
      duration: 1500,
      isClosable: true,
      position: "bottom",
    });
  };

  const handleEdit = (e) => {
    e?.stopPropagation();
    handleClick();
  };

  const handleRefine = (e) => {
    e?.stopPropagation();
    if (onRefineWithPabs) {
      const label = fieldLabel || alt || 'this section';
      const currentContent = typeof value === 'string' ? value : (Array.isArray(value) ? value.join('\n') : String(value || ''));
      const prompt = `Help me refine the ${label}. Here's what I have currently:\n\n"${currentContent.substring(0, 500)}${currentContent.length > 500 ? '...' : ''}"\n\nPlease improve this and apply the update.`;
      const refinementCtx = {
        sourceView: 'strategy',
        sectionTitle: label,
      };
      onRefineWithPabs(prompt, refinementCtx);
    }
  };

  // Lets a parent render its own action row (e.g. a card header) and still
  // trigger this field's copy/edit/refine behavior instead of duplicating it.
  useImperativeHandle(ref, () => ({
    copyValue: () => handleCopy(),
    startEditing: () => handleClick(),
    triggerRefine: () => handleRefine(),
  }));

  useEffect(() => {
    if (isEditing && textareaRef.current && isMultiLine) {
      textareaRef.current.focus();
    }
  }, [isEditing, isMultiLine]);

  useEffect(() => {
    if (isEditing) return; // Don't overwrite user's in-progress edits
    let newEditValue;
    if (typeof value === 'string') {
      newEditValue = value || '';
    } else if (Array.isArray(value)) {
      newEditValue = value.join('\n');
    } else if (value && typeof value === 'object') {
      newEditValue = JSON.stringify(value, null, 2);
    } else if (value === null || value === undefined) {
      newEditValue = '';
    } else {
      newEditValue = String(value);
    }
    const preprocessed = preprocessContent(newEditValue);
    setEditValue(preprocessed);
  }, [value, isEditing]);

  const stringContentFromValue = React.useMemo(() => {
    if (typeof value === 'string') {
      return value;
    } else if (Array.isArray(value)) {
      return value.join('\n');
    } else if (value && typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    } else if (value === null || value === undefined) {
      return '';
    } else {
      return String(value);
    }
  }, [value]);

  const renderContent = () => {
    // Normalize AI-generated markdown: remove spaces adjacent to ** and * markers
    // e.g. "** bold **" → "**bold**", "* italic *" → "*italic*"
    // Use [ \t]+ (not \s+) so we never match across newlines.
    // e.g. "** bold **" → "**bold**" but "**line1**\n**line2**" stays intact.
    const normalized = stringContentFromValue
      .replace(/\*\*[ \t]+(.+?)[ \t]+\*\*/g, '**$1**')
      .replace(/\*\*[ \t]+(.+?)\*\*/g, '**$1**')
      .replace(/\*\*(.+?)[ \t]+\*\*/g, '**$1**')
      .replace(/(?<!\*)\*[ \t]+(.+?)[ \t]+\*(?!\*)/g, '*$1*');
    return (
      <ReactMarkdown components={fieldMarkdownComponents}>
        {normalized}
      </ReactMarkdown>
    );
  };

  const hasContent = value && (typeof value === 'string' ? value.trim() : true);

  const renderActionButtons = () => {
    if (!hasContent || actionsPlacement === 'none') return null;
    
    return (
      <HStack
        spacing={0}
        justify="flex-end"
        mt={actionsPlacement === 'top' ? 0 : 1}
        mb={actionsPlacement === 'top' ? 1 : 0}
      >
        <Tooltip label="Copy" hasArrow placement="top" fontSize="xs">
          <IconButton
            icon={<CopyIcon />}
            size="xs"
            variant="ghost"
            color="gray.700"
            aria-label="Copy content"
            onClick={handleCopy}
            _hover={{ color: 'black', bg: 'gray.100' }}
            minW="28px"
            h="28px"
          />
        </Tooltip>
        {!isReadOnly && (
          <Tooltip label="Edit" hasArrow placement="top" fontSize="xs">
            <IconButton
              icon={<EditIcon />}
              size="xs"
              variant="ghost"
              color="gray.700"
              aria-label="Edit content"
              onClick={handleEdit}
              _hover={{ color: 'black', bg: 'gray.100' }}
              minW="28px"
              h="28px"
            />
          </Tooltip>
        )}
        {!isReadOnly && onRefineWithPabs && (
          <Tooltip label="Refine with Pabs" hasArrow placement="top" fontSize="xs">
            <IconButton
              icon={
                <span className="material-icons-outlined" style={{ fontSize: '15px' }}>
                  auto_fix_high
                </span>
              }
              size="xs"
              variant="ghost"
              color="gray.700"
              aria-label="Refine with Pabs"
              onClick={handleRefine}
              _hover={{ color: 'black', bg: 'gray.100' }}
              minW="28px"
              h="28px"
            />
          </Tooltip>
        )}
      </HStack>
    );
  };

  if (isReadOnly) {
    return (
      <Box>
        <Box
          className="editable-box read-only"
          sx={{
            lineHeight: '1.5',
            padding: '8px',
            border: '1px solid transparent',
            borderRadius: '4px',
            width: '100%',
            minWidth: '100%',
            boxSizing: 'border-box',
            color: value ? '#2d3748' : '#a0aec0',
          }}
        >
          {value ? renderContent() : <span style={{ color: '#a0aec0' }}>{placeholder || 'No content'}</span>}
        </Box>
        {hasContent && actionsPlacement !== 'none' && (
          <HStack spacing={0} justify="flex-end" mt={1}>
            <Tooltip label="Copy" hasArrow placement="top" fontSize="xs">
              <IconButton
                icon={<CopyIcon />}
                size="xs"
                variant="ghost"
                color="gray.700"
                aria-label="Copy content"
                onClick={handleCopy}
                _hover={{ color: 'black', bg: 'gray.100' }}
                minW="28px"
                h="28px"
              />
            </Tooltip>
          </HStack>
        )}
      </Box>
    );
  }
  
  return isEditing ? (
    <Box>
      {isMultiLine ? (
        <TextareaAutosize
          ref={textareaRef}
          value={editValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          autoFocus
          placeholder={placeholder}
          className="editable-field-textarea"
          minRows={2}
          style={{
            width: '100%',
            padding: '8px 16px',
            fontSize: 'inherit',
            fontFamily: 'inherit',
            lineHeight: '1.5',
            borderRadius: '6px',
            border: '1px solid #E2E8F0',
            backgroundColor: 'white',
            outline: 'none',
            boxShadow: '0 0 0 1px #3182ce',
            resize: 'none',
          }}
        />
      ) : (
        <Input
          value={editValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          autoFocus
          placeholder={placeholder}
          alt={alt}
          sx={{
            lineHeight: '1.5',
            padding: '8px',
            border: '1px solid transparent',
            borderRadius: '6px !important',
            backgroundColor: '#f4f8ff',
            width: '100%',
            minWidth: '100%',
            boxSizing: 'border-box',
            height: 'auto !important',
            minHeight: 'auto !important',
            _hover: {
              backgroundColor: '#FFFFFF',
            },
            color: editValue ? '#2d3748' : '#a0aec0',
            _placeholder: {
              color: '#a0aec0',
            },
          }}
        />
      )}
    </Box>
  ) : (
    <Box>
      {actionsPlacement === 'top' && renderActionButtons()}
      <Tooltip label={alt} hasArrow placement="top">
        <Box
          onClick={handleClick}
          cursor="pointer"
          className="editable-box"
          sx={{
            lineHeight: '1.5',
            padding: '8px',
            border: '1px solid transparent',
            borderRadius: '4px',
            width: '100%',
            minWidth: '100%',
            boxSizing: 'border-box',
            color: value ? '#2d3748' : 'brand.500',
            _hover: {
              backgroundColor: 'brand.200',
              color: value ? '#1a202c' : '#718096',
            },
          }}
        >
          {value ? renderContent() : <span>{placeholder}</span>}
        </Box>
      </Tooltip>
      {actionsPlacement !== 'top' && renderActionButtons()}
    </Box>
  );
});

EditableField.displayName = 'EditableField';

export default EditableField;
